import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner, useForm } from '@bringg/react-components';
import { observer } from 'mobx-react';
import { useOnScreen } from '@bringg-frontend/hooks';
import isEqual from 'lodash/isEqual';

import { DAYS_OF_WEEK } from './constants';
import { buildFormValues, buildRowsForSave, emptyRow, isEmptyRow } from './helpers';
import { ReserveCapacityFormValues, RowFormValue, ViewMode } from './types';
import { DaySection } from './components/day-section';
import { FormControls } from './components/form-controls';
import { ReserveCapacityHeader } from './components/reserve-capacity-header';
import { ReserveCapacityStore } from './store/reserve-capacity-store';
import { ReserveCapacityStoreProvider, useReserveCapacityStore } from './store/store-context';
import { useUnsavedChangesGuard } from './hooks/use-unsaved-changes-guard';

import styles from './reserve-capacity.module.scss';

const initialValues: ReserveCapacityFormValues = {
	allDaysRows: [emptyRow()],
	perDayRows: DAYS_OF_WEEK.map(() => [emptyRow()])
};

const ReserveCapacityContent = observer(() => {
	const { t } = useTranslation();
	const store = useReserveCapacityStore();
	const [form] = useForm();
	const containerRef = useRef<HTMLDivElement>(null);
	const isVisible = useOnScreen(containerRef);
	const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.allDays);
	const [isDirty, setIsDirty] = useState(false);

	useEffect(() => {
		if (store.data.length) {
			form.setFieldsValue(buildFormValues(store.data));
		}

		setViewMode(
			store.data.length > 0 && store.data.every(row => typeof row.weekDay === 'number')
				? ViewMode.perDay
				: ViewMode.allDays
		);
		setIsDirty(false);
	}, [store.data, form]);

	useEffect(() => {
		if (isVisible) {
			void store.fetchConfig();
		}
	}, [isVisible, store]);

	useUnsavedChangesGuard(isDirty);

	const handleValuesChanged = (_: unknown, values: ReserveCapacityFormValues): void => {
		store.resetSaveFailed();

		const stripEmpty = (rows: RowFormValue[]) => (rows ?? []).filter(row => !isEmptyRow(row));
		const normalize = (v: ReserveCapacityFormValues) => ({
			allDaysRows: stripEmpty(v.allDaysRows),
			perDayRows: (v.perDayRows ?? []).map(stripEmpty)
		});
		const normalizedCurrent = normalize(values);
		const normalizedSaved = normalize(store.data.length ? buildFormValues(store.data) : initialValues);

		if (viewMode === ViewMode.allDays) {
			setIsDirty(!isEqual(normalizedCurrent.allDaysRows, normalizedSaved.allDaysRows));
		} else {
			setIsDirty(!isEqual(normalizedCurrent.perDayRows, normalizedSaved.perDayRows));
		}
	};

	const handleFinish = async (values: ReserveCapacityFormValues) => {
		const rows = buildRowsForSave(values, viewMode);

		await store.saveConfig(rows);
	};

	const handleDiscard = (): void => {
		const savedValues = store.data.length ? buildFormValues(store.data) : initialValues;

		form.resetFields();
		form.setFieldsValue(savedValues);
		setViewMode(
			store.data.length > 0 && store.data.every(row => typeof row.weekDay === 'number')
				? ViewMode.perDay
				: ViewMode.allDays
		);
		setIsDirty(false);
		store.resetSaveFailed();
	};

	let content = null;

	if (!isVisible || store.isLoading) {
		content = (
			<div className={styles.loading}>
				<Spinner />
			</div>
		);
	} else if (store.fetchFailed) {
		content = (
			<div className={styles.fetchError}>
				<span>{t('RESERVE_CAPACITY.FETCH_FAILED')}</span>
				<Button onClick={() => void store.fetchConfig()}>{t('RESERVE_CAPACITY.RETRY')}</Button>
			</div>
		);
	} else {
		content = (
			<Form
				form={form}
				initialValues={initialValues}
				onSubmit={handleFinish}
				onValuesChange={handleValuesChanged}
			>
				<>
					<FormControls viewMode={viewMode} onViewModeChange={setViewMode} />
					<div className={styles.formScrollArea}>
						{viewMode === ViewMode.allDays ? (
							<DaySection name="allDaysRows" title={t('RESERVE_CAPACITY.ALL_DAYS')} form={form} />
						) : (
							DAYS_OF_WEEK.map(({ key, abbr }, idx) => (
								<DaySection
									key={key}
									name={['perDayRows', idx]}
									title={t(key)}
									abbreviation={t(abbr)}
									form={form}
								/>
							))
						)}
					</div>
					<div className={styles.formFooter}>
						<Button type="default" disabled={!isDirty || store.isSaving} onClick={handleDiscard}>
							{t('RESERVE_CAPACITY.DISCARD')}
						</Button>
						<Button
							htmlType="submit"
							type="primary"
							danger={store.saveFailed}
							title={store.saveFailed ? t('RESERVE_CAPACITY.SAVE_FAILED') : ''}
							disabled={!isDirty}
							loading={store.isSaving}
						>
							{t('RESERVE_CAPACITY.SAVE')}
						</Button>
					</div>
				</>
			</Form>
		);
	}

	return (
		<div ref={containerRef} className={styles.container}>
			<ReserveCapacityHeader />
			{content}
		</div>
	);
});

export const ReserveCapacity = () => {
	const store = useMemo(() => new ReserveCapacityStore(), []);

	return (
		<ReserveCapacityStoreProvider value={store}>
			<ReserveCapacityContent />
		</ReserveCapacityStoreProvider>
	);
};
